import React, { useEffect, useMemo, useRef } from 'react';
import { useDialogManager } from 'src/components/widgets/dialog';
import { showNotification } from 'src/components/widgets/notificationToaster';
import { EcommerceId } from 'src/shared/enums';
import { T, toLocalID } from 'src/shared/helpers';
import { editColorNameAndAttributesAction, editModelColorMappingsSalesChannelsAction, useProductConfigurationState } from '../../state';
import type { variantsQuery$data } from './__generated__/variantsQuery.graphql';
import type { ConnectedVariantStoreOption, VariantStoreOptions } from './useVariantStoreOptions';
import type { merchantProductOptionType } from './helpers';
import RepairVariantMappingsDialog from './repairVariantMappingsDialog';
import { buildVariantMappingRepairs, type ChannelMappingRepair, getApplicableRepairStrategyIDs, type RepairEntry } from './variantMappingRepair';

import { buildMultiChannelVariantMappingUpdates } from './variantMappingRepairUpdates';
import type { StoreOption } from './variantMappingRepairStrategies/types';

type RepairSource = Pick<ChannelMappingRepair, 'salesChannelID' | 'label' | 'isMain' | 'productSKU'> & {
    entries: RepairEntry[];
    options: readonly StoreOption[];
    isShopify: boolean;
};

const buildChannelRepair = ({ entries, options, isShopify, ...channel }: RepairSource): ChannelMappingRepair => ({
    ...channel,
    context: { options, isShopify },
    availableStrategies: getApplicableRepairStrategyIDs({ options, isShopify }),
    repairs: buildVariantMappingRepairs(entries, options, isShopify)
});

type UseVariantMappingRepairsOptions = Pick<variantsQuery$data, 'salesChannels'> & {
    merchantProductOptions: VariantStoreOptions;
    merchantProductOptionsConnected: readonly ConnectedVariantStoreOption[];
    unsupportedSalesChannelsVariantsIDs: readonly string[];
    allOptionsReady: boolean;
    onMainOptionsChange: (options: merchantProductOptionType[]) => void;
};

export function useVariantMappingRepairs({
    salesChannels,
    merchantProductOptions,
    merchantProductOptionsConnected,
    unsupportedSalesChannelsVariantsIDs,
    allOptionsReady,
    onMainOptionsChange
}: UseVariantMappingRepairsOptions) {
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const currentSalesChannel = salesChannels.find((channel) => channel.id === state.salesChannelID);
    const isFromScratch = currentSalesChannel?.ecommerceSettings?.ecommerceName === 'NONE';
    const channelMappingRepairs = useMemo(() => {
        const mainChannelID = toLocalID(state.salesChannelID);
        const mainSources: RepairSource[] =
            !isFromScratch && merchantProductOptions.length > 0
                ? [
                      {
                          salesChannelID: state.salesChannelID,
                          label: currentSalesChannel?.ecommerceSettings?.remoteUrl || T._('Main store', 'Admin'),
                          isMain: true,
                          productSKU: state.product.code,
                          options: merchantProductOptions,
                          isShopify: currentSalesChannel?.ecommerceSettings?.ecommerceID === EcommerceId.SHOPIFY,
                          entries: state.product.colors.map((color) => ({ key: color.guid, variantGuid: color.guid, name: color.name, code: color.code }))
                      }
                  ]
                : [];
        const connectedSources = state.product.additionalCodes.flatMap<RepairSource>((additionalCode) => {
            const channelID = toLocalID(additionalCode.salesChannelId);
            const channel = salesChannels.find((candidate) => toLocalID(candidate.id) === channelID);
            if (channelID === mainChannelID || !channel || unsupportedSalesChannelsVariantsIDs.includes(channel.id)) return [];
            const options = merchantProductOptionsConnected.filter(
                (option) => option.salesChannelID === channelID && (option.sku == null || option.sku === additionalCode.code)
            );
            // An absent catalog cannot establish a safe repair (e.g. a deleted product or unavailable store).
            if (!options.length) return [];
            const entries = state.product.colorMappings.flatMap((mapping, index) => {
                if (toLocalID(mapping.salesChannelID) !== channelID || (mapping.productSKU && mapping.productSKU !== additionalCode.code)) return [];
                const color = state.product.colors.find((candidate) =>
                    mapping.productColor?.guid ? candidate.guid === mapping.productColor.guid : candidate.colorID === mapping.colorID
                );
                return color ? [{ key: String(index), variantGuid: color.guid, name: color.name, code: mapping.merchantCode }] : [];
            });
            return [
                {
                    salesChannelID: additionalCode.salesChannelId,
                    label:
                        additionalCode.remoteUrl ||
                        channel.ecommerceSettings?.remoteUrl ||
                        channel.ecommerceSettings?.ecommerceName ||
                        additionalCode.salesChannelId,
                    isMain: false,
                    productSKU: additionalCode.code,
                    options,
                    entries,
                    isShopify: channel.ecommerceSettings?.ecommerceID === EcommerceId.SHOPIFY
                }
            ];
        });
        return [...mainSources, ...connectedSources]
            .filter(
                (source, index, sources) => sources.findIndex((candidate) => toLocalID(candidate.salesChannelID) === toLocalID(source.salesChannelID)) === index
            )
            .map(buildChannelRepair)
            .filter((channel) => channel.repairs.length > 0);
    }, [
        state.product.code,
        state.product.colors,
        state.product.colorMappings,
        state.product.additionalCodes,
        state.salesChannelID,
        merchantProductOptions,
        salesChannels,
        merchantProductOptionsConnected,
        isFromScratch,
        currentSalesChannel,
        unsupportedSalesChannelsVariantsIDs
    ]);
    const mismatchedVariantGuids = new Set(channelMappingRepairs.flatMap((channel) => channel.repairs.map((repair) => repair.variantGuid)));
    const hasMappingMismatch = mismatchedVariantGuids.size > 0;

    // Dialogs retain the callback from when they opened; validate against the latest committed state.
    const latest = useRef({ product: state.product, channels: channelMappingRepairs, merchantProductOptions, allOptionsReady });
    useEffect(() => {
        latest.current = { product: state.product, channels: channelMappingRepairs, merchantProductOptions, allOptionsReady };
    }, [state.product, channelMappingRepairs, merchantProductOptions, allOptionsReady]);

    const applyChannelRepairs = (channels: ChannelMappingRepair[], allChannels: boolean) => {
        const current = latest.current;
        const updates =
            current.product.id === state.product.id && (!allChannels || current.allOptionsReady)
                ? buildMultiChannelVariantMappingUpdates(current.product, channels, current.channels, allChannels)
                : null;
        if (!updates) {
            closeDialog('repair-variant-mappings');
            showNotification({
                type: 'danger',
                message: T._('The repair preview is no longer valid. No repairs were applied. Reopen bulk repairs to review the current mappings.', 'Admin'),
                dismiss: { duration: 6000 }
            });
            return;
        }
        const { colorUpdates, colorMappings } = updates;
        colorUpdates.forEach(({ color, attributes }) => dispatch(editColorNameAndAttributesAction(color, color.name, attributes)));
        dispatch(editModelColorMappingsSalesChannelsAction(colorMappings));
        if (channels.some((channel) => channel.isMain)) {
            onMainOptionsChange(current.merchantProductOptions.map((option) => ({ ...option, values: option.values.map((value) => ({ ...value })) })));
        }
        closeDialog('repair-variant-mappings');
        showNotification({
            type: 'success',
            message: T._('Variant mapping repairs applied. Save the product to persist the changes.', 'Admin'),
            dismiss: { duration: 5000 }
        });
    };

    const openRepairDialog = () => {
        if (hasMappingMismatch) {
            showDialog(
                'repair-variant-mappings',
                <RepairVariantMappingsDialog channels={channelMappingRepairs} allOptionsReady={allOptionsReady} onConfirm={applyChannelRepairs} />
            );
        }
    };

    return { hasMappingMismatch, mismatchedVariantGuids, openRepairDialog };
}
